import { Armor, initArmor } from '../../mechanics/items/Armor'
import { Ally } from '../ally/Ally'
import { Enemy } from '../enemies/Enemy'
import Inventory, { initInventory } from '../inventory/Inventory'
import { initWeapon, Weapon } from '../items/Weapon'
import { soundToPlay } from '../sounds/sound'

export interface IImages {
    img: string
    webp: string
}

interface IEquipment {
    armor: Armor
    weapon: Weapon
}

type TConditionName = "bleeding" | "poison" | "stun"

export interface ICharacterLevel {
    curruntLevel: number,
    currentExperience: number,
    experianceToLevelUp: number
    itemToStatUpName: string
    itemToStatUpAmount: number
    itemToStatUpNeed: number
}

interface ICondition {
    description: string,
    duration: number,
    name: TConditionName,
    damage: number
    icon: string
}

export interface ISkill {
    images: IImages
    dis: string
}

interface IUpgradeStatValue {
    upgradeStatValue: number
}

export type TStatName = "Constitution" | "Dexterety" | "Strength" | "Charm" | "Wisdom" | "Intelligent"

export type IObjectOfImprovedStats = {
    [statName in TStatName]: IUpgradeStatValue
}

export interface ICharacterStats {
    initHp: number,
    initAttack: number,
    initIsNpc: boolean,
    initName: string,
    initImgSmall: IImages,
    initImgBig: IImages,
    initDexterety: number,
    initStrength: number,
    initConstitution: number,
    initWisdom: number,
    initCharm: number,
    initIntelligent: number,
    initGold: number,
    initSkills: ISkill[],
    initEquipment: IEquipment
}

export default class Character {
    private hp: number
    private maxHp: number
    private armor: number
    private attack: number
    private isNpc: boolean
    private name: string
    private imgSmall: IImages
    private imgBig: IImages
    private skills: ISkill[]
    private constitution: number
    private dexterety: number
    private intelligent: number
    private charm: number
    private wisdom: number
    private conditions: ICondition[]
    protected isDead: boolean
    protected strength: number
    protected damage: number
    protected gold: number
    protected skillsCooldown: number[]
    protected equipment: IEquipment
    protected level: ICharacterLevel
    protected inventory: Inventory
    protected allies: Ally[]
    protected improvedStats: IObjectOfImprovedStats
    protected isNpTurn: boolean

    constructor(characterStats: ICharacterStats) {
        this.constitution = characterStats.initConstitution
        this.dexterety = characterStats.initDexterety
        this.hp = characterStats.initHp + this.constitution
        this.maxHp = this.hp
        // this.maxHp = this.hp
        // this.hp = this.calcMod(this.constitution) + characterStats.initHp
        this.armor = this.calcMod(this.dexterety) + 10
        this.strength = characterStats.initStrength
        this.intelligent = characterStats.initIntelligent
        this.charm = characterStats.initCharm
        this.wisdom = characterStats.initWisdom
        this.attack = characterStats.initAttack
        this.isNpc = characterStats.initIsNpc
        this.name = characterStats.initName
        this.imgSmall = characterStats.initImgSmall
        this.imgBig = characterStats.initImgBig
        this.damage = 4
        this.gold = characterStats.initGold
        this.skills = characterStats.initSkills
        this.skillsCooldown = [0, 0, 0, 0]
        // this.equipment = { armor: new Armor(initArmor), weapon: new Weapon(initWeapon) }
        this.equipment = characterStats.initEquipment
        this.isDead = false
        this.conditions = []
        this.level = {
            currentExperience: 0,
            experianceToLevelUp: 100,
            curruntLevel: 1,
            itemToStatUpName: 'stress',
            itemToStatUpAmount: 0,
            itemToStatUpNeed: 1
        }
        this.inventory = new Inventory(initInventory)
        this.allies = []
        this.improvedStats = {
            "Constitution": { upgradeStatValue: 0 },
            "Dexterety": { upgradeStatValue: 0 },
            "Strength": { upgradeStatValue: 0 },
            "Charm": { upgradeStatValue: 0 },
            "Wisdom": { upgradeStatValue: 0 },
            "Intelligent": { upgradeStatValue: 0 },
        }
        this.isNpTurn = false
    }

    setHp(newHp: number) {
        this.hp = newHp
        if (this.hp <= 0)
            this.isDead = true
    }

    getHp() {
        return this.hp
    }

    getMaxHp() {
        if (this.isNpc)
            return this.maxHp

        return this.maxHp + this.calcMod(this.getCon()) + this.allyMod('hp')
    }

    resetHp() {
        this.hp = this.maxHp
    }

    getAttack() {
        return Math.floor(Math.random() * (20 - 1 + 1) + 1) + this.attack + this.calcMod(this.getStr())
    }

    getArmor() {
        return this.armor + this.equipment.armor.getArmorValue() + this.calcMod(this.getDex())
    }

    getImgSmall() { return this.imgSmall }

    getImgBig() { return this.imgBig }

    getName() {
        return this.name
    }

    selfHeal(value: number) {
        if (this.hp + value > this.maxHp) {
            this.hp = this.maxHp
        }
        else {
            this.hp = this.hp + value
        }
    }

    getIsNpc() {
        return this.isNpc
    }

    getGold() {
        return this.gold
    }

    setGold(newGold: number) {
        this.gold = newGold
    }

    getInt() {
        return this.intelligent + this.allyMod('int') + this.lvlMod('Intelligent')
    }

    getCon() {
        return this.constitution + this.allyMod('con') + this.lvlMod('Constitution')
    }

    getDex() {
        return this.dexterety + this.allyMod('dex') + this.lvlMod('Dexterety')
    }

    getStr() {
        return this.strength + this.allyMod('str') + this.lvlMod('Strength')
    }

    getChr() {
        return this.charm + this.lvlMod('Charm')
    }

    getWis() {
        return this.wisdom + this.allyMod('wis') + this.lvlMod('Wisdom')
    }

    getSkills() {
        return this.skills
    }

    getDamage() {
        return this.equipment.weapon.weaponType ? this.damage + this.equipment.weapon.getWeaponDamage() : this.damage
    }

    setIsDead(value: boolean) {
        this.isDead = value
    }

    getIsDead() {
        return this.isDead
    }

    getLevel() {
        return this.level
    }

    getInventory() {
        return this.inventory
    }

    private setLevel(newLevel: ICharacterLevel) {
        this.level = newLevel
    }

    addLevelExp(value: number) {
        this.level.currentExperience += value
        if (this.level.currentExperience >= this.level.experianceToLevelUp)
            this.addLevel()
    }

    private addLevel() {
        const newCurrentLevel = this.level.curruntLevel + 1
        const newExperianceToLevelUp = this.level.experianceToLevelUp + Math.floor(150 * Math.pow(1.1, newCurrentLevel))
        const newLevel: ICharacterLevel = {
            ...this.level,
            curruntLevel: newCurrentLevel,
            experianceToLevelUp: newExperianceToLevelUp,
            itemToStatUpAmount: this.level.itemToStatUpAmount + newCurrentLevel - 1
        }

        this.setLevel(newLevel)
    }

    pushCondition(condition: ICondition) {
        this.conditions.push(condition)
    }

    dealDamage(dmgToCharacter: Character | Enemy) {
        this.decSkillsCooldown()

        if (this.getAttack() >= dmgToCharacter.getArmor()) {
            const dmg = Math.floor(Math.random() * (this.getDamage() - 1 + 1) + 1) + this.calcMod(this.getStr())
            dmgToCharacter.setHp(dmgToCharacter.getHp() - dmg)
            soundToPlay("damageDeal")
        }
    }

    npcDealDamage(dmgToCharacter: Character) {
        this.decSkillsCooldown()

        if (this.getAttack() >= dmgToCharacter.getArmor()) {
            const dmg = Math.floor(Math.random() * (this.getDamage() - 1 + 1) + 1) + this.calcMod(this.getStr())
            dmgToCharacter.setHp(dmgToCharacter.getHp() - dmg)
            soundToPlay("damageDeal")
        }
    }

    firstSkill(dmgToCharacter: Character | Character[], enemiesIndex: number) { }

    secondSkill(dmgToCharacter: Character | Character[], enemiesIndex: number) { }

    thirdSkill(dmgToCharacter: Character | Character[], enemiesIndex: number) { }

    getskillsCooldown() {
        return this.skillsCooldown
    }

    resetSkillsCooldowm() {
        this.skillsCooldown = [0, 0, 0, 0]
    }

    decSkillsCooldown() {
        for (let i = 0; i < this.skillsCooldown.length; i++) {
            if (this.skillsCooldown[i] > 0)
                this.skillsCooldown[i]--
        }
    }

    setSkillCooldown(index: number, cooldownValue: number) {
        this.skillsCooldown[index] = cooldownValue
    }

    // используется другая функция, npcDealDamage. Maybe delete
    doNpcLogic(playerCharacter: Character) {
        if (this.hp <= 0)
            return
        // this.dealDamage(playerCharacter)
    }

    calcMod(ability: number) {
        return ability % 2 === 0 ? (ability - 10) / 2 : (ability - 11) / 2
    }

    wearingArmor(item: Armor) {
        this.equipment.armor = item
    }

    removeArmor() {
        this.equipment.armor = new Armor(initArmor)
    }

    wearingWeapon(item: Weapon) {
        this.equipment.weapon = item
    }

    removeWeapon() {
        this.equipment.weapon = new Weapon(initWeapon)
    }

    getEquipment() {
        return this.equipment
    }

    addGold(value: number) {
        this.gold = this.gold + value
    }

    getConditions() {
        return this.conditions
    }

    addActiveAlly(ally: Ally) {
        if (this.allies.length < 6 && !ally.getIsActive()) {
            ally.setIsActive(true)
            this.allies.push(ally)
        }
    }

    removeActiveAlly(ally: Ally) {
        if (ally.getIsActive()) {
            ally.setIsActive(false)
            this.allies.splice(this.allies.indexOf(ally), 1)
        }
    }

    allyMod(modName: string) {
        let bountyValue = 0

        this.allies.forEach(ally => {
            if (ally.getBounty().name === modName) {
                bountyValue = ally.getBounty().value[ally.getLevel().curruntLevel]
            }
        })

        return bountyValue
    }

    getImprovedStats() {
        return this.improvedStats
    }

    improveStat(statName: TStatName[], newStats: IObjectOfImprovedStats) {
        // проверяем кол-во прилетевших статов и их стоимость
        let sumStat = 0

        Object.values(newStats).map(item => sumStat += item.upgradeStatValue)

        let costOfSumStat = this.level.itemToStatUpNeed

        for (let i = 1; i < sumStat; i++) {
            costOfSumStat += this.level.itemToStatUpNeed + i
        }
        // если поинтов не хватает ничего не делаем
        if (this.level.itemToStatUpAmount < costOfSumStat) return
        // если поинтов хватает сетаем новые значения
        for (let i = 0; i < statName.length; i++) {
            this.improvedStats = {
                ...this.improvedStats,
                [statName[i]]: { upgradeStatValue: this.improvedStats[statName[i]].upgradeStatValue + newStats[statName[i]].upgradeStatValue }
            }
        }
        // увеличиваем кол-во которое нужно на след апгрейд, меняем кол-во которое есть
        this.level.itemToStatUpNeed = this.level.itemToStatUpNeed + sumStat
        this.level.itemToStatUpAmount = this.level.itemToStatUpAmount - costOfSumStat
    }

    lvlMod(statName: TStatName) {
        return this.improvedStats[statName].upgradeStatValue
    }
}